import { useMemo, useState } from "react";
import { Wand2, Copy } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { sanitizeCameraIdInput, cameraIdForIndex, findDuplicateCameraIds } from "@produce/shared";
import { copyCameraIdsFromPreviousDay, previousCameraDay } from "@/lib/cameraIds";

/**
 * One assignable Camera ID slot: a single person on a single day. A
 * person with several camera/photo functions that day is still ONE
 * slot (one camera, one ID) — the parent collapses functions into
 * `functions` and keeps every matching crew row in sync via `onChange`.
 */
export interface CameraIdRow {
  /** Stable unique key per (person, day). */
  key: string;
  name: string;
  /** Camera/photo functions this person holds that day (for display). */
  functions: string[];
  /** ISO yyyy-mm-dd. */
  eventDate: string;
  cameraId: string;
}

interface CameraIdCardProps {
  rows: CameraIdRow[];
  onChange: (key: string, cameraId: string) => void;
}

/**
 * Producer card for assigning Camera IDs to camera operators /
 * photographers. Camera IDs are unique PER DAY, so on a multi-day
 * production each day is edited independently (day tabs) and the
 * auto-assign + duplicate detection operate within the selected day.
 */
export function CameraIdCard({ rows, onChange }: CameraIdCardProps) {
  // Distinct days that actually have a camera/photo crew member, in
  // chronological order.
  const days = useMemo(() => Array.from(new Set(rows.map((r) => r.eventDate))).sort(), [rows]);
  const [activeDay, setActiveDay] = useState<string | null>(null);
  const selectedDay = activeDay && days.includes(activeDay) ? activeDay : days[0];

  // Copy the previous day's Camera IDs onto `targetDay` for everyone who
  // worked both days. Same person keeps the same ID across days, so this
  // saves re-typing. No-op (and no toast) when nothing changes.
  const handleCopyFromPreviousDay = (targetDay: string) => {
    const fromDay = previousCameraDay(days, targetDay);
    if (!fromDay) return;
    const changes = copyCameraIdsFromPreviousDay(rows, targetDay, fromDay);
    if (changes.length === 0) {
      toast.info("Nothing to copy — no shared crew with a Camera ID on the previous day.");
      return;
    }
    changes.forEach((c) => onChange(c.key, c.cameraId));
    toast.success(
      `Copied ${changes.length} Camera ID${changes.length === 1 ? "" : "s"} from the previous day.`,
    );
  };

  if (rows.length === 0) {
    return (
      <Card className="bg-card p-6">
        <CameraIdHeader />
        <p className="text-sm text-muted-foreground">
          No camera operators or photographers in the crew yet. Add crew with a video or photo
          function to assign Camera IDs.
        </p>
      </Card>
    );
  }

  return (
    <Card className="bg-card p-6">
      <CameraIdHeader />
      {days.length > 1 ? (
        <Tabs value={selectedDay} onValueChange={setActiveDay} className="w-full">
          <TabsList className="mb-4 flex flex-wrap">
            {days.map((day) => (
              <TabsTrigger key={day} value={day}>
                {format(new Date(day), "EEE d MMM")}
              </TabsTrigger>
            ))}
          </TabsList>
          {days.map((day, index) => (
            <TabsContent key={day} value={day}>
              <CameraIdDayList
                rows={rows.filter((r) => r.eventDate === day)}
                onChange={onChange}
                onCopyFromPreviousDay={index > 0 ? () => handleCopyFromPreviousDay(day) : undefined}
              />
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <CameraIdDayList rows={rows} onChange={onChange} />
      )}
    </Card>
  );
}

function CameraIdHeader() {
  return (
    <div className="mb-4">
      <h3 className="mb-1 text-lg font-semibold">Camera ID</h3>
      <p className="text-sm text-muted-foreground">
        Assign a Camera ID (1–3 letters, e.g. <span className="font-medium">A</span> or{" "}
        <span className="font-medium">JES</span>) to each camera operator and photographer so the
        editor knows who shot the footage. Camera IDs must be unique per day.
      </p>
    </div>
  );
}

/** The per-day list of name / function / Camera ID input rows. */
function CameraIdDayList({
  rows,
  onChange,
  onCopyFromPreviousDay,
}: {
  rows: CameraIdRow[];
  onChange: (key: string, cameraId: string) => void;
  /** Provided (and shown) only for days after the first. */
  onCopyFromPreviousDay?: () => void;
}) {
  const duplicateKeys = useMemo(
    () => findDuplicateCameraIds(rows.map((r) => ({ key: r.key, cameraId: r.cameraId }))),
    [rows],
  );

  // Auto-assign A, B, C… in the order the crew appears, within this day.
  const handleAutoAssign = () => {
    rows.forEach((row, i) => onChange(row.key, cameraIdForIndex(i)));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        {onCopyFromPreviousDay && (
          <Button type="button" variant="outline" size="sm" onClick={onCopyFromPreviousDay}>
            <Copy className="mr-2 h-4 w-4" />
            Copy from previous day
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={handleAutoAssign}>
          <Wand2 className="mr-2 h-4 w-4" />
          Auto-assign
        </Button>
      </div>

      {rows.map((row) => {
        const isDuplicate = duplicateKeys.has(row.key);
        return (
          <div
            key={row.key}
            className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-background p-3"
          >
            <div className="min-w-[8rem] flex-1">
              <div className="font-medium">{row.name || "Unnamed"}</div>
              <div className="text-sm capitalize text-muted-foreground">
                {row.functions.join(", ")}
              </div>
            </div>
            <div className="flex flex-col items-end">
              <Input
                value={row.cameraId}
                onChange={(e) => onChange(row.key, sanitizeCameraIdInput(e.target.value))}
                placeholder="ID"
                aria-label={`Camera ID for ${row.name}`}
                aria-invalid={isDuplicate}
                className={`w-24 text-center uppercase ${
                  isDuplicate ? "border-destructive focus-visible:ring-destructive" : ""
                }`}
              />
              {isDuplicate && <span className="mt-1 text-xs text-destructive">Duplicate ID</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
